import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getWalletDetail,
  updateWalletStatus,
  manualWalletAdjustment,
  getTransactions // For wallet transactions
} from '../../services/api';
import { 
  FiArrowLeft, 
  FiRefreshCw, 
  FiUser, 
  FiCreditCard, 
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiCopy,
  FiChevronRight,
  FiCalendar,
  FiMail,
  FiPhone,
  FiShield,
  FiLoader,
  FiLock,
  FiUnlock,
  FiActivity,
  FiTrendingUp,
  FiChevronUp,
  FiChevronDown
} from 'react-icons/fi';
import { 
  TbArrowsExchange,
  TbReceiptRefund,
  TbWallet,
  TbBuildingStore
} from 'react-icons/tb';

function WalletDetail() {
  const { walletId } = useParams();
  const navigate = useNavigate();
  
  const [wallet, setWallet] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [payoutMethods, setPayoutMethods] = useState([]);
  const [adjustmentData, setAdjustmentData] = useState({
    amount: '',
    type: 'credit',
    reason: '',
    description: ''
  });
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    fetchWalletDetail();
  }, [walletId]);

  const fetchWalletDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getWalletDetail(walletId);
      
      if (response.success) {
        setWallet(response.data.wallet);
        setRecentTransactions(response.data.recent_transactions || []);
        setPayoutMethods(response.data.payout_methods || []); // Add this line
      } else {
        setError(response.message || 'Failed to load wallet details');
      }
    } catch (err) {
      setError('Failed to load wallet details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Add this function to format payout method details
  const getPayoutMethodBadge = (methodType) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    
    const methodConfig = {
      'bank_account': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Bank Account', icon: '🏦' },
      'card': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Card', icon: '💳' },
      'mobile_money': { bg: 'bg-green-100', text: 'text-green-800', label: 'Mobile Money', icon: '📱' },
      'paypal': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'PayPal', icon: 'P' },
      'crypto': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Crypto', icon: '₿' }
    };
  
    const config = methodConfig[methodType] || { bg: 'bg-gray-100', text: 'text-gray-800', label: methodType, icon: '💰' };
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const getVerificationStatus = (isVerified) => {
    if (isVerified === 1) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Verified</span>;
    } else {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
    }
  };

  const PayoutMethodCard = ({ method }) => {
    const [expanded, setExpanded] = useState(false);
  
    return (
      <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            {getPayoutMethodBadge(method.method_type)}
            {method.is_default === 1 && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                Default
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {getVerificationStatus(method.is_verified)}
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-gray-400 hover:text-gray-600"
            >
              {expanded ? <FiChevronUp /> : <FiChevronDown />}
            </button>
          </div>
        </div>
  
        {method.recipient_code && (
          <div className="text-sm text-gray-600 mb-2">
            <span className="font-medium">Recipient Code:</span> {method.recipient_code}
          </div>
        )}
  
        {expanded && method.details && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Details</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(method.details).map(([key, value]) => (
                <div key={key} className="flex flex-col">
                  <span className="text-gray-500 text-xs">{key.replace(/_/g, ' ')}:</span>
                  <span className="font-medium">{value || 'N/A'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
  
        <div className="flex justify-between items-center mt-3 text-xs text-gray-500">
          <span>Created: {formatDate(method.created_at)}</span>
          <span>Updated: {formatDate(method.updated_at)}</span>
        </div>
      </div>
    );
  };

  const fetchWalletTransactions = async () => {
    try {
      const response = await getTransactions({ wallet_id: walletId, limit: 50 });
      if (response.success) {
        setRecentTransactions(response.data.transactions || []);
      }
    } catch (err) {
      console.error('Failed to load transactions:', err);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    const action = newStatus === 'frozen' ? 'freeze' : 'unfreeze';
    if (!window.confirm(`Are you sure you want to ${action} this wallet?`)) {
      return;
    }

    try {
      const reason = prompt(`Enter reason for ${action} (optional):`, '');
      const response = await updateWalletStatus(walletId, newStatus, reason || '');

      if (response.success) {
        setSuccessMessage(`Wallet ${action}d successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchWalletDetail();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      setError(`Failed to ${action} wallet: ${err.message}`);
    }
  };

  const handleManualAdjustment = async () => {
    if (!adjustmentData.amount || parseFloat(adjustmentData.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!adjustmentData.reason.trim()) {
      setError('Please enter a reason for the adjustment');
      return;
    }

    try {
      setAdjusting(true);
      setError('');
      const response = await manualWalletAdjustment(
        walletId,
        parseFloat(adjustmentData.amount),
        adjustmentData.type,
        adjustmentData.reason,
        adjustmentData.description || 'Manual adjustment by admin'
      );

      if (response.success) {
        setSuccessMessage(`Wallet adjusted successfully! ${adjustmentData.type === 'credit' ? '+' : '-'}${formatCurrency(adjustmentData.amount)}`);
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowAdjustmentModal(false);
        setAdjustmentData({
          amount: '',
          type: 'credit',
          reason: '',
          description: ''
        });
        fetchWalletDetail();
        fetchWalletTransactions();
      } else {
        throw new Error(response.message || 'Adjustment failed');
      }
    } catch (err) {
      setError(`Failed to adjust wallet: ${err.message}`);
    } finally {
      setAdjusting(false);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-4 py-2 text-sm font-medium rounded-full flex items-center justify-center";
    
    const statusConfig = {
      'active': { bg: 'bg-green-100', text: 'text-green-800', label: 'Active', icon: FiCheckCircle },
      'frozen': { bg: 'bg-red-100', text: 'text-red-800', label: 'Frozen', icon: FiLock },
      'suspended': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Suspended', icon: FiAlertCircle },
      'inactive': { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Inactive', icon: FiXCircle }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: FiAlertCircle };
    const Icon = config.icon;
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        <Icon className="mr-2" />
        {config.label}
      </span>
    );
  };

  const getVerificationBadge = (status) => {
    const baseClasses = "px-3 py-1 text-xs font-medium rounded-full";
    
    if (status === 1) {
      return <span className={`${baseClasses} bg-green-100 text-green-800`}>Verified</span>;
    } else if (status === 0) {
      return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
    } else if (status === -1) {
      return <span className={`${baseClasses} bg-red-100 text-red-800`}>Rejected</span>;
    }
    
    return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Unknown</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    
    const statusConfig = {
      'successful': { bg: 'bg-green-100', text: 'text-green-800', label: 'Success', icon: '✓' },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending', icon: '⏳' },
      'failed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed', icon: '✗' },
      'cancelled': { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Cancelled', icon: '⊘' },
      'reversed': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Reversed', icon: '↻' }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: '?' };
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const getTransactionTypeBadge = (type) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    
    if (type === 'credit') {
      return <span className={`${baseClasses} bg-green-100 text-green-800`}>Credit</span>;
    } else if (type === 'debit') {
      return <span className={`${baseClasses} bg-red-100 text-red-800`}>Debit</span>;
    }
    
    return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{type}</span>;
  };

  const calculateStatistics = () => {
    if (!recentTransactions.length) return null;
    
    const stats = {
      totalTransactions: recentTransactions.length,
      totalCredits: 0,
      totalDebits: 0,
      successfulTransactions: 0,
      failedTransactions: 0,
      averageTransaction: 0,
      largestTransaction: 0
    };
    
    let totalAmount = 0;
    
    recentTransactions.forEach(tx => {
      const amount = parseFloat(tx.amount) || 0;
      totalAmount += amount;
      
      if (tx.type === 'credit') {
        stats.totalCredits += amount;
      } else if (tx.type === 'debit') {
        stats.totalDebits += amount;
      }
      
      if (tx.status === 'successful') {
        stats.successfulTransactions++;
      } else if (tx.status === 'failed') {
        stats.failedTransactions++;
      }
      
      if (amount > stats.largestTransaction) {
        stats.largestTransaction = amount;
      }
    });
    
    stats.averageTransaction = totalAmount / stats.totalTransactions;
    stats.netFlow = stats.totalCredits - stats.totalDebits;
    
    return stats;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="flex items-center mb-8">
            <div className="h-10 w-10 bg-gray-200 rounded-lg mr-4"></div>
            <div>
              <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-48"></div>
            </div>
          </div>

          {/* Main Card Skeleton */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i}>
                  <div className="h-4 bg-gray-100 rounded w-32 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-48"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs Skeleton */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="h-8 bg-gray-100 rounded w-48 mb-6"></div>
            <div className="h-40 bg-gray-50 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin/finance/wallets')}
            className="flex items-center text-gray-600 hover:text-gray-800 mr-4"
          >
            <FiArrowLeft className="mr-2" />
            Back
          </button>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 text-center">
          <FiAlertCircle className="text-red-500 text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Wallet</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => navigate('/admin/finance/wallets')}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
            >
              Back to Wallets
            </button>
            <button
              onClick={fetchWalletDetail}
              className="px-6 py-3 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!wallet) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="text-center py-16">
          <div className="text-gray-300 text-8xl mb-6">💳</div>
          <h3 className="text-xl font-medium text-gray-700 mb-2">Wallet Not Found</h3>
          <p className="text-gray-500 mb-6">The wallet you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/finance/wallets')}
            className="px-6 py-3 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] font-medium"
          >
            Back to Wallets
          </button>
        </div>
      </div>
    );
  }

  const stats = calculateStatistics();

  return (
    <div className="min-h-full bg-[#f5f5f5] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center mb-4">
              <button
                onClick={() => navigate('/admin/finance/wallets')}
                className="flex items-center text-gray-600 hover:text-gray-800 mr-4 transition-colors"
              >
                <FiArrowLeft className="mr-2" />
                Back to Wallets
              </button>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-[#0f0e0e]">Wallet Details</h1>
              <div className="flex items-center space-x-2">
                {getStatusBadge(wallet.status)}
                <span className="bg-gray-100 px-3 py-1 rounded-lg text-sm">
                  ID: {wallet.wallet_id}
                </span>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600">
              <div className="flex items-center">
                <FiUser className="mr-2" />
                <span className="font-medium">{wallet.first_name} {wallet.last_name}</span>
              </div>
              <div className="flex items-center">
                <FiMail className="mr-2" />
                <span>{wallet.email}</span>
                <button
                  onClick={() => copyToClipboard(wallet.email)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                  title="Copy Email"
                >
                  <FiCopy />
                </button>
              </div>
              <div className="flex items-center">
                <FiCalendar className="mr-2" />
                <span>Created: {formatDate(wallet.created_at)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-3">
            <button
              onClick={() => setShowAdjustmentModal(true)}
              className="px-4 py-2.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all duration-200 font-medium flex items-center"
            >
              <TbArrowsExchange className="mr-2" />
              Manual Adjustment
            </button>
            <button
              onClick={fetchWalletDetail}
              className="px-4 py-2.5 bg-white text-[#0f0e0e] border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-500 text-xl" />
            <p className="text-green-700 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-700 hover:text-green-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-500 text-xl" />
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Column - Wallet Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Wallet Summary Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center">
                <TbWallet className="mr-2 text-[#ff660e]" />
                Wallet Summary
              </h2>
              <div className="text-3xl font-bold text-[#0f0e0e]">
                {formatCurrency(wallet.balance)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Wallet Status</label>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(wallet.status)}
                    <span className="text-sm text-gray-500">
                      {wallet.status === 'active' ? '✅ Operational' : '❌ Restricted'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Currency</label>
                  <p className="text-lg font-medium text-[#0f0e0e]">
                    {wallet.currency}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Created</label>
                  <div className="flex items-center text-gray-600">
                    <FiCalendar className="mr-2" />
                    <span>{formatDateTime(wallet.created_at)}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated</label>
                  <div className="flex items-center text-gray-600">
                    <FiClock className="mr-2" />
                    <span>{formatDateTime(wallet.updated_at)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">User ID</label>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-600">{wallet.user_id}</span>
                    <button
                      onClick={() => copyToClipboard(wallet.user_id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
                  <div className="flex items-center text-gray-600">
                    <FiPhone className="mr-2" />
                    <span>{wallet.phone_number || 'N/A'}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">User Created</label>
                  <div className="text-gray-600">
                    {formatDate(wallet.user_created_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Store Information Card */}
          {(wallet.store_name || wallet.store_description) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
                <TbBuildingStore className="mr-2 text-[#ff660e]" />
                Store Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Store Name</label>
                    <p className="text-lg font-medium text-[#0f0e0e]">
                      {wallet.store_name || 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Seller Verification</label>
                    <div className="flex items-center space-x-2">
                      {getVerificationBadge(wallet.seller_verification_status)}
                      <span className="text-sm text-gray-500">
                        {wallet.seller_verification_status === 1 ? 'Verified Seller' : 'Not Verified'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Store Description</label>
                  <p className="text-gray-600">
                    {wallet.store_description || 'No description provided'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Payout Methods Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center">
                <FiCreditCard className="mr-2 text-[#ff660e]" />
                Payout Methods
                <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                  {payoutMethods.length}
                </span>
              </h2>
            </div>

            {payoutMethods.length > 0 ? (
              <div className="space-y-3">
                {payoutMethods.map((method) => (
                  <div key={method.payout_method_id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          method.method_type === 'bank_account' ? 'bg-blue-100 text-blue-800' :
                          method.method_type === 'card' ? 'bg-purple-100 text-purple-800' :
                          method.method_type === 'mobile_money' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {method.method_type === 'bank_account' ? '🏦 Bank Account' :
                          method.method_type === 'card' ? '💳 Card' :
                          method.method_type === 'mobile_money' ? '📱 Mobile Money' :
                          '💰 ' + method.method_type}
                        </span>
                        {method.is_default === 1 && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {method.is_verified === 1 ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Verified
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </div>
                    </div>

                    {method.recipient_code && (
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Recipient Code:</span> 
                        <span className="ml-2 font-mono text-xs">{method.recipient_code}</span>
                        <button
                          onClick={() => copyToClipboard(method.recipient_code)}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                          title="Copy"
                        >
                          <FiCopy className="w-3 h-3" />
                        </button>
                      </div>
                    )}

                    {method.details && (
                      <div className="mt-2">
                        <div className="text-sm text-gray-500 mb-1">Account Details:</div>
                        <div className="grid grid-cols-2 gap-2 text-sm bg-gray-50 p-2 rounded">
                          {Object.entries(method.details).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-gray-500">{key.replace(/_/g, ' ')}:</span>
                              <span className="ml-1 font-medium">{value || 'N/A'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between items-center mt-3 text-xs text-gray-500">
                      <div className="flex items-center">
                        <FiCalendar className="mr-1" />
                        <span>Added: {formatDate(method.created_at)}</span>
                      </div>
                      {method.updated_at && method.updated_at !== method.created_at && (
                        <div className="flex items-center">
                          <FiClock className="mr-1" />
                          <span>Updated: {formatDate(method.updated_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-300 text-6xl mb-4">💳</div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Payout Methods</h3>
                <p className="text-gray-500">This user hasn't added any payout methods yet.</p>
              </div>
            )}
          </div>

          {/* Recent Transactions Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center">
                <FiActivity className="mr-2 text-[#ff660e]" />
                Recent Transactions
              </h2>
              <button
                onClick={() => navigate(`/admin/finance/transactions?wallet_id=${wallet.wallet_id}`)}
                className="text-sm text-[#ff660e] hover:text-[#e55c0d] font-medium flex items-center"
              >
                View All Transactions
                <FiChevronRight className="ml-1" />
              </button>
            </div>

            {recentTransactions.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentTransactions.map((transaction) => (
                  <div
                    key={transaction.transaction_id}
                    className="p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getTransactionTypeBadge(transaction.type)}
                        {getTransactionStatusBadge(transaction.status)}
                        <span className="text-xs text-gray-500">
                          {transaction.transaction_type?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className={`text-lg font-bold ${
                        transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'credit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-1">
                      {transaction.description}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="font-mono">{transaction.reference}</span>
                      <span>{formatDateTime(transaction.created_at)}</span>
                    </div>
                    
                    {transaction.verified_at && (
                      <div className="text-xs text-green-600 mt-1">
                        ✓ Verified: {formatDateTime(transaction.verified_at)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-300 text-6xl mb-4">💳</div>
                <p className="text-gray-500">No transactions found for this wallet</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Actions & Stats */}
        <div className="space-y-6">
          {/* Status Actions Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
              <FiShield className="mr-2 text-[#ff660e]" />
              Status Management
            </h2>

            <div className="space-y-3">
              {wallet.status === 'active' ? (
                <button
                  onClick={() => handleStatusUpdate('frozen')}
                  className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium flex items-center justify-center border border-red-200 transition-colors"
                >
                  <FiLock className="mr-2" />
                  Freeze Wallet
                </button>
              ) : wallet.status === 'frozen' ? (
                <button
                  onClick={() => handleStatusUpdate('active')}
                  className="w-full px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium flex items-center justify-center border border-green-200 transition-colors"
                >
                  <FiUnlock className="mr-2" />
                  Unfreeze Wallet
                </button>
              ) : null}

              <div className="pt-4 mt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Current Status</p>
                <div className="flex justify-center">
                  {getStatusBadge(wallet.status)}
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Card */}
          {stats && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
                <FiTrendingUp className="mr-2 text-[#ff660e]" />
                Transaction Statistics
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Total Transactions</label>
                  <p className="text-2xl font-bold text-[#0f0e0e]">{stats.totalTransactions}</p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Successful:</span>
                    <span className="font-medium text-green-600">{stats.successfulTransactions}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Failed:</span>
                    <span className="font-medium text-red-600">{stats.failedTransactions}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Net Flow:</span>
                    <span className={`font-medium ${stats.netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stats.netFlow >= 0 ? '+' : ''}{formatCurrency(stats.netFlow)}
                    </span>
                  </div>
                </div>
                
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Largest Transaction:</span>
                    <span className="font-medium">{formatCurrency(stats.largestTransaction)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Average Transaction:</span>
                    <span className="font-medium">{formatCurrency(stats.averageTransaction)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
              <FiActivity className="mr-2 text-[#ff660e]" />
              Quick Actions
            </h2>

            <div className="space-y-3">
              <button
                onClick={() => navigate(`/admin/finance/transactions?wallet_id=${wallet.wallet_id}`)}
                className="w-full px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg font-medium flex items-center justify-center border border-gray-200 transition-colors"
              >
                <FiActivity className="mr-2" />
                View All Transactions
              </button>
              
              <button
                onClick={() => navigate(`/admin/finance/payments?user_id=${wallet.user_id}`)}
                className="w-full px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg font-medium flex items-center justify-center border border-gray-200 transition-colors"
              >
                <FiCreditCard className="mr-2" />
                View Payment History
              </button>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(wallet, null, 2));
                  alert('Wallet details copied to clipboard!');
                }}
                className="w-full px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg font-medium flex items-center justify-center border border-gray-200 transition-colors"
              >
                <FiCopy className="mr-2" />
                Copy Wallet Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Adjustment Modal */}
      {showAdjustmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-[#0f0e0e]">
                  Manual Wallet Adjustment
                </h3>
                <button
                  onClick={() => setShowAdjustmentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiXCircle className="text-xl" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">
                  Wallet: <span className="font-medium">{wallet.first_name} {wallet.last_name}</span>
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  Current Balance: <span className="font-bold">{formatCurrency(wallet.balance)}</span>
                </p>
                <p className="text-sm text-gray-600">
                  User ID: <span className="font-mono">{wallet.user_id}</span>
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adjustment Type
                  </label>
                  <select 
                    value={adjustmentData.type}
                    onChange={(e) => setAdjustmentData({...adjustmentData, type: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                  >
                    <option value="credit">Credit (+ Add Funds)</option>
                    <option value="debit">Debit (- Remove Funds)</option>
                    <option value="bonus">Bonus (Free Credit)</option>
                    <option value="penalty">Penalty (Deduction)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (NGN)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₦</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={adjustmentData.amount}
                      onChange={(e) => setAdjustmentData({...adjustmentData, amount: e.target.value})}
                      placeholder="0.00"
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason *
                  </label>
                  <input
                    type="text"
                    value={adjustmentData.reason}
                    onChange={(e) => setAdjustmentData({...adjustmentData, reason: e.target.value})}
                    placeholder="e.g., Manual adjustment, Refund, etc."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={adjustmentData.description}
                    onChange={(e) => setAdjustmentData({...adjustmentData, description: e.target.value})}
                    placeholder="Additional details..."
                    rows="2"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowAdjustmentModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleManualAdjustment}
                  disabled={adjusting || !adjustmentData.amount || !adjustmentData.reason}
                  className="px-4 py-2 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {adjusting ? (
                    <>
                      <FiLoader className="mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Apply Adjustment'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default WalletDetail;